/* React Higher Order Component which fetches short course data for project where shortCourseId = props.shortCourseId
    Child (InnerComponent) will only render once short course data is successfully fetched.
    child will receive props: {
        shortCourseId: PropTypes.number,
        fatalError: PropTypes.object,
        shortCourseStoreState: PropTypes.shape({ initialized: PropTypes.bool, shortCourse: PropTypes.object, ... })
    }

*/

import React from 'react';
import PropTypes from 'prop-types';
const uuid = require('uuid');

const ShortCourseActions = require('../../actions/ShortCourseActions');
const ShortCourseStore = require('../../stores/ShortCourseStore');
import LoadingMainContent from '../loading/LoadingMainContent';
import ErrorBox from '../common/ErrorBox';
const ReactUtil = require('../../util/ReactUtil');

const propTypes = {
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    shortCourseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    forceRefreshOnMount: PropTypes.bool, // force refresh of short course data, even if short course store already has data for the given id
    hideLoadingIcon: PropTypes.bool
};

const defaultProps = {
    forceRefreshOnMount: false,
    hideLoadingIcon: false
};

export default function withShortCourseData(WrappedComponent) {
    class WithShortCourseData extends React.Component {
        constructor(props) {
            super(props);

            this.state = {
                getActionId: null,
                fatalError: null
            }

            this.getShortCourseData = this.getShortCourseData.bind(this);
            this.unsubscribes = [ShortCourseStore.listen(this.onShortCourseStoreChange.bind(this))];
        }

        componentDidMount() {
            const newShortCourseStoreState = ShortCourseStore.getStateShortCourse(this.props.shortCourseId, this.props.projectId, this.props.channelId);
            if (
                (
                    !newShortCourseStoreState.initialized
                    || !newShortCourseStoreState.shortCourse
                )
                || this.props.forceRefreshOnMount
            ) {
                this.getShortCourseData(this.props.channelId, this.props.projectId, this.props.shortCourseId);
            }
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            if (
                this.props.channelId !== nextProps.channelId
                || this.props.projectId !== nextProps.projectId
                || this.props.shortCourseId !== nextProps.shortCourseId
            ) {
                const newShortCourseStoreState = ShortCourseStore.getStateShortCourse(nextProps.shortCourseId, nextProps.projectId, nextProps.channelId);
                if (
                    (
                        !newShortCourseStoreState.initialized
                        || !newShortCourseStoreState.shortCourse
                    )
                    || this.props.forceRefreshOnMount
                ) {
                    this.getShortCourseData(nextProps.channelId, nextProps.projectId, nextProps.shortCourseId);
                }
            }
        }

        componentWillUnmount() {
            this.unsubscribes.forEach(u => u());
        }

        getShortCourseData(channelId, projectId, shortCourseId) {
            const actionId = uuid.v4();
            this.setState({ getActionId: actionId }, () => {
                ShortCourseActions.getShortCourseWithLatestDoc(actionId, {
                        shortCourseId: shortCourseId,
                        channelId: channelId
                    });
            });
        }

        onShortCourseStoreChange(newState) {
            let updatedState = {};
            if (newState.action.id === this.state.getActionId) {
                updatedState = { ...updatedState, getActionId: null };
                if (!!newState.action.error) {
                    updatedState = { ...updatedState, fatalError: newState.action.error };
                }
            }

            this.setState({
                ...updatedState,
            });
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} allowStackTrace={false} />;
            }

            const shortCourseStoreState = ShortCourseStore.getStateShortCourse(this.props.shortCourseId, this.props.projectId, this.props.channelId);
            if (!shortCourseStoreState.initialized || !shortCourseStoreState.shortCourse) {
                if (this.props.hideLoadingIcon) {
                    return null;
                }
                else {
                    return <LoadingMainContent />;
                }
            }

            return (
                <WrappedComponent
                    {...this.props}
                    shortCourseStoreState={shortCourseStoreState}
                />
            );
        }
    }

    WithShortCourseData.propTypes = propTypes;
    WithShortCourseData.defaultProps = defaultProps;
    WithShortCourseData.displayName = `WithShortCourseData(${ReactUtil.getComponentDisplayName(WrappedComponent)})`
    return WithShortCourseData;
}
